import { type ReactNode, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { CustomDomainRouter } from "@/components/routing/CustomDomainRouter";
import { ScrollToTop } from "@/components/routing/ScrollToTop";
import { EntranceGate } from "@/components/routing/EntranceGate";
import { setHydrating } from "@/lib/hydration";

const queryClient = new QueryClient();

/**
 * Everything inside the router. Shared with the pre-build renderer (src/prerender/entry.tsx),
 * which wraps it in a StaticRouter, so both produce the same markup.
 */
export function AppContent({ queryClient, prebuilt, children }: { queryClient: QueryClient; prebuilt: boolean; children: (routes: ReactNode) => ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        {children(
          <>
            <ScrollToTop />
            <EntranceGate prebuilt={prebuilt}>
              <CustomDomainRouter />
            </EntranceGate>
          </>,
        )}
      </TooltipProvider>
    </QueryClientProvider>
  );
}

/** `prebuilt`: the page was delivered as pre-built HTML and is being hydrated. */
const App = ({ prebuilt = false }: { prebuilt?: boolean }) => {
  useEffect(() => setHydrating(false), []);
  return <AppContent queryClient={queryClient} prebuilt={prebuilt}>{routes => <BrowserRouter>{routes}</BrowserRouter>}</AppContent>;
};

export default App;
